import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { isEmpty, isNil } from 'lodash'
import pluralize from 'pluralize'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'

import { Button } from '@/components/buttons/Button'
import { toast } from '@/components/toasts/toast.helpers'
import { UNCATEGORIZED_TAGS } from '@/utils/constants'
import { Breadcrumbs } from '@/components/forms/Breadcrumbs/Breadcrumbs'
import { PageHeader } from '@/components/headers/PageHeader'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { isCategoryNameTooLong } from '@/views/Organizations/helpers'
import {
  createCategory,
  updateTagsInState,
  updateUsers,
  updateTagCategories,
  updateCategory,
  deleteTag,
  deleteAllTags,
  deleteCategory,
  createTags,
} from '@/reducers/organizations/organizationTagManager.actions'

import { AddTagsCard } from './AddTagsCard'
import { AddCategoryForm } from './AddCategoryForm'
import { UncategorizedTagsCard } from './UncategorizedTagsCard'
import { CategoryCard } from './CategoryCard'
import {
  buildUpdatedUsers,
  moveTagInState,
  reorderColumns,
  computeSortOrderUpdates,
  createDragEndHandler,
  hasDuplicateTags,
  isDuplicateCategory,
} from './tagBoard.helpers'

const TagForm = ({
  tags,
  organizationId,
  fetchCategoriesAndTags,
  users,
  name,
  userOrganizationId,
  edit_users,
}) => {
  const dispatch = useDispatch()
  const deleteModalObject = {
    header: '',
    message: '',
    deleteFunc: {},
    display: false,
  }
  const [show, setShow] = useState(false)
  const [category, setCategory] = useState('')
  const [tagGroup, setTagGroup] = useState('')
  const [groupTags, setGroupTags] = useState('')
  const [deleteModal, setDeleteModal] = useState(deleteModalObject)

  const tagCategories = []
  let allTags = []
  if (tags && tags.columnOrder) {
    tags.columnOrder.forEach((columnId) => {
      if (tags.column[columnId]?.title !== UNCATEGORIZED_TAGS) {
        tagCategories.push({
          key: tags.column[columnId]?.categoryId,
          label: tags.column[columnId]?.title,
          value: tags.column[columnId]?.categoryId,
        })
      }
    })
    allTags = Object.keys(tags.tags)
  }

  // Returns the fetch promise so callers that await the reload (createCategory's
  // success toast) fire only once the board reflects the change.
  const reloadTags = ({ organizationId: orgId }) => dispatch(fetchCategoriesAndTags(orgId))

  const handleCloseDeletionModal = () => {
    setDeleteModal(deleteModalObject)
  }

  const removeAllTags = () => {
    dispatch(deleteAllTags({ organizationId, reloadTags }))
    setDeleteModal(deleteModalObject)
  }

  const handleDropdownChange = (option) => {
    setCategory(option ? option.key : '')
  }

  const createTagsLocally = () => {
    const tagNames = tagGroup
      .split(',')
      .map((tagName) => tagName.trim())
      .filter(Boolean)
    if (hasDuplicateTags({ existingTags: tags.tags, newNames: tagNames })) {
      toast.error('Duplicate tags')
      return
    }
    if (!isEmpty(tagNames)) {
      dispatch(
        createTags({
          tags: { tagNames },
          category,
          organizationId,
          reloadTags,
        })
      )
    }

    setTagGroup('')
  }

  const addGroupTags = () => {
    if (isCategoryNameTooLong(groupTags)) {
      return
    }
    if (isDuplicateCategory({ columns: tags.column, name: groupTags })) {
      toast.error('Duplicate categories')
      return
    }
    dispatch(
      createCategory({
        tagCategories: groupTags,
        organizationId,
        reloadTags,
        allCategories: tags,
      })
    )
    setGroupTags('')
  }

  const deleteTagFromStore = (deletedTag) => {
    dispatch(
      deleteTag({
        tag: deletedTag,
        // Users reload too: deleting a tag cascades into user_tags
        reloadTags,
        organizationId,
      })
    )
    setDeleteModal(deleteModalObject)
  }

  const openDeleteTagFromStoreModal = (deletedTag) => {
    setDeleteModal({
      header: 'Delete tag',
      message: `Are you sure you would like to delete "${deletedTag?.content}"? It will be removed from all of your organization's users, scorecards, leaderboards, team filters, and alerts. All alerts that are set to this tag only will be disabled.`,
      deleteFunc: () => deleteTagFromStore(deletedTag),
      display: true,
    })
  }

  const updateCategoryFromStore = (selectedCategory, catName) => {
    dispatch(
      updateCategory({
        category: selectedCategory,
        updates: { name: catName },
        reloadTags,
        organizationId,
      })
    )
  }

  const deleteCategoryLocally = async (selectedCategory) => {
    await dispatch(
      deleteCategory({
        category: selectedCategory,
        reloadTags,
        organizationId,
      })
    )
    setDeleteModal(deleteModalObject)
  }

  const openDeleteCategoryModal = (deletedCategory) => {
    setDeleteModal({
      header: 'Delete category',
      message: `Are you sure you want to delete "${deletedCategory.title}"? It will unstage all tags associated with its category.`,
      deleteFunc: () => deleteCategoryLocally(deletedCategory),
      display: true,
    })
  }

  const openModalDeleteAllTags = () => {
    setDeleteModal({
      header: 'Remove all tags',
      message: 'Are you sure you want to delete all tags?',
      deleteFunc: () => removeAllTags(),
      display: true,
    })
  }

  const handleTagGroup = (event) => {
    setTagGroup(event.target.value)
  }

  const updateTagCategoriesLocal = (result) => {
    const destinationOrg = tags.column[result.destination.droppableId]
    const droppedTag = tags.tags[result.draggableId]
    dispatch(updateTagCategories(droppedTag.tagId, destinationOrg))
  }

  const saveTags = (result = {}) => {
    const newUsers = buildUpdatedUsers({
      users,
      column: tags.column,
      result,
      draggableId: result.draggableId,
    })
    updateTagCategoriesLocal(result)
    // Keep users in redux current so a drag doesn't require a hard reload
    dispatch(updateUsers(newUsers))
    dispatch(updateTagsInState(moveTagInState({ tagsState: tags, result })))
  }

  const reorderCategories = (result) => {
    dispatch(updateTagsInState(reorderColumns({ tagsState: tags, result })))
    const { affected, moved } = computeSortOrderUpdates({
      categories: Object.values(tags.column),
      oldIndex: result.source.index,
      newIndex: result.destination.index,
      draggableId: result.draggableId,
    })
    const updatePromises = affected.map(({ category: affectedCategory, updates }) =>
      dispatch(
        updateCategory({
          category: affectedCategory,
          updates,
          reloadTags: () => {},
          organizationId,
        })
      )
    )
    Promise.all(updatePromises).then(() => {
      dispatch(
        updateCategory({
          category: moved.category,
          updates: moved.updates,
          reloadTags,
          organizationId,
        })
      )
    })
  }

  const onDragEnd = createDragEndHandler({
    tagsState: tags,
    users,
    onColumnReorder: reorderCategories,
    onTagMove: saveTags,
    onConflict: () => setShow(true),
  })

  const displayBreadcrumb = (userOrgId) => {
    const backLinks = {
      label: userOrgId === 1 ? name : 'users',
      link: userOrgId === 1 ? `/organizations/${organizationId}` : '/users',
    }

    return <Breadcrumbs backLinks={backLinks} currentLink="Manage Tags" />
  }

  const renderBoard = () => {
    if (isNil(tags) || isNil(tags.columnOrder)) {
      return null
    }
    const uncategorizedColumn = Object.values(tags.column).find(
      (column) => column.title === UNCATEGORIZED_TAGS
    )
    const categorizedCount = Object.values(tags.tags).filter(
      (tag) => !isNil(tag.tagCategoryId)
    ).length
    const columnTags = (column) => column.taskIds.map((taskId) => tags.tags[taskId])

    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="manage-tags__section-heading">
          <h2>All tags</h2>
          <span className="manage-tags__section-meta">
            {`${allTags.length} ${pluralize('tag', allTags.length)} · ${
              tagCategories.length
            } ${pluralize('category', tagCategories.length)}`}
          </span>
        </div>
        {uncategorizedColumn && (
          <UncategorizedTagsCard
            column={uncategorizedColumn}
            tags={columnTags(uncategorizedColumn)}
            deleteTag={openDeleteTagFromStoreModal}
          />
        )}

        <div className="manage-tags__section-heading manage-tags__section-heading-row">
          <h2>Categories</h2>
          <span className="manage-tags__section-meta">{`${categorizedCount} categorized`}</span>
          {edit_users && (
            <div className="ml-auto">
              <AddCategoryForm
                value={groupTags}
                onChange={(event) => setGroupTags(event.target.value)}
                onSubmit={addGroupTags}
              />
            </div>
          )}
        </div>
        <Droppable droppableId="all-columns" direction="horizontal" type="column">
          {(provided) => (
            <div
              className="manage-tags__categories-row"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {tags.columnOrder.map((columnId, index) => {
                const column = tags.column[columnId]
                if (column?.title === UNCATEGORIZED_TAGS) {
                  return null
                }
                return (
                  <CategoryCard
                    key={column.id}
                    column={column}
                    tags={columnTags(column)}
                    index={index}
                    edit_users={edit_users}
                    deleteCategory={openDeleteCategoryModal}
                    updateCategory={updateCategoryFromStore}
                    deleteTag={openDeleteTagFromStoreModal}
                  />
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    )
  }

  return (
    <>
      {deleteModal.display && (
        <BasicModal
          title={deleteModal.header}
          onClose={handleCloseDeletionModal}
          show={deleteModal.display}
          size="tiny"
        >
          <div data-testid="confirm-delete-modal">{deleteModal.message}</div>
          <ModalActions
            onClose={handleCloseDeletionModal}
            onSubmit={deleteModal.deleteFunc}
            isDelete
          />
        </BasicModal>
      )}

      <BasicModal title="Warning" onClose={() => setShow(false)} show={show} size="tiny">
        <p>
          Moving this tag is prevented as it will cause a user to have more than one assigned tag
          from this category.
        </p>
        <div className="modal-footer">
          <Button onClick={() => setShow(false)}>Close</Button>
        </div>
      </BasicModal>

      <PageHeader
        breadcrumbs={displayBreadcrumb(userOrganizationId)}
        actions={
          userOrganizationId === 1 ? (
            <Button destructive onClick={openModalDeleteAllTags} disabled={isEmpty(allTags)}>
              Remove all tags
            </Button>
          ) : undefined
        }
      />
      {edit_users && (
        <AddTagsCard
          tagGroup={tagGroup}
          onTagGroupChange={handleTagGroup}
          category={category}
          onCategoryChange={handleDropdownChange}
          tagCategories={tagCategories}
          onSubmit={createTagsLocally}
        />
      )}
      {renderBoard()}
    </>
  )
}

export default withLDConsumer()(TagForm)
